import { lazy, Suspense, type ComponentType, type ReactElement, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router";

import { LoadingScreen } from "@/components/loading-screen";
import { AuthProvider } from "@/hooks/use-auth";

const SignIn = lazy(() => import("./routes/auth/sign-in"));
const AppLayout = lazy(() => import("./routes/app-layout"));
const Dashboard = lazy(() => import("./routes/app/dashboard"));
const Sessions = lazy(() => import("./routes/app/sessions"));
const SessionDetail = lazy(() => import("./routes/app/session-detail"));
const LiteLLMLogs = lazy(() => import("./routes/app/litellm-logs"));
const PropertyClassifications = lazy(() => import("./routes/app/property-classifications"));
const PromptsIndex = lazy(() => import("./routes/app/prompts-list"));
const PromptTemplateDetail = lazy(() => import("./routes/app/prompts/slug"));
const PromptVersionNew = lazy(() => import("./routes/app/prompts/version-new"));
const PromptVersionShow = lazy(() => import("./routes/app/prompts/version-show"));
const PromptCompare = lazy(() => import("./routes/app/prompts/compare"));
const NotFound = lazy(() => import("./routes/not-found"));
const AccessDenied = lazy(() => import("./routes/access-denied"));
const ProtectedRoute = lazy(() => import("./routes/protected-route"));

const withSuspense = (Component: ComponentType): ReactElement => (
  <Suspense fallback={<LoadingScreen />}>
    <Component />
  </Suspense>
);

// AuthProvider lives under the router so it can read/write URL search params.
function RootShell({ children }: { children: ReactNode }): ReactElement {
  return <AuthProvider>{children}</AuthProvider>;
}

function RootElement(): ReactElement {
  return (
    <RootShell>
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    </RootShell>
  );
}

const router = createBrowserRouter([
  {
    element: <RootElement />,
    children: [
      { path: "/login", element: <Navigate to="/sign-in" replace /> },
      { path: "/sign-in", element: withSuspense(SignIn) },
      { path: "/access-denied", element: withSuspense(AccessDenied) },
      {
        element: withSuspense(ProtectedRoute),
        children: [
          {
            element: withSuspense(AppLayout),
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              { path: "dashboard", element: withSuspense(Dashboard) },
              { path: "sessions", element: withSuspense(Sessions) },
              { path: "sessions/:sessionId", element: withSuspense(SessionDetail) },
              { path: "litellm", element: withSuspense(LiteLLMLogs) },
              { path: "classifications", element: withSuspense(PropertyClassifications) },
              { path: "prompts", element: withSuspense(PromptsIndex) },
              { path: "prompts/:slug", element: withSuspense(PromptTemplateDetail) },
              { path: "prompts/:slug/versions/new", element: withSuspense(PromptVersionNew) },
              { path: "prompts/:slug/versions/:id", element: withSuspense(PromptVersionShow) },
              { path: "prompts/:slug/compare", element: withSuspense(PromptCompare) },
            ],
          },
        ],
      },
      { path: "*", element: withSuspense(NotFound) },
    ],
  },
]);

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
